import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

/**
 * SignaturePad — HTML5 canvas drawing pad.
 *
 * Ref API:
 *   ref.current.getDataURL()  → base64 PNG string (or null if blank)
 *   ref.current.clear()       → clears the canvas
 *   ref.current.isEmpty()     → true if no strokes drawn
 */
const SignaturePad = forwardRef(({ width = "100%", height = 180, onChange }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  // ─── Expose methods via ref ─────────────────────────────────
  useImperativeHandle(ref, () => ({
    getDataURL: () => {
      if (!hasStrokes) return null;
      return canvasRef.current?.toDataURL("image/png") || null;
    },
    clear: () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasStrokes(false);
      onChange?.(null);
    },
    isEmpty: () => !hasStrokes,
  }));

  // ─── Resize canvas to match container ──────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      // Preserve existing drawing at new size
      const snapshot = canvas.toDataURL();
      canvas.width = rect.width;
      canvas.height = rect.height || height;
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1e1b4b";
      ctx.lineWidth = 2.2;
      // Restore
      if (hasStrokes) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0);
        img.src = snapshot;
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  // ─── Pointer helpers ────────────────────────────────────────
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = e.touches ? e.touches[0] : e;
    return {
      x: source.clientX - rect.left,
      y: source.clientY - rect.top,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);

    // Smooth bezier curve between last and current point
    const midX = (lastPos.current.x + pos.x) / 2;
    const midY = (lastPos.current.y + pos.y) / 2;
    ctx.quadraticCurveTo(lastPos.current.x, lastPos.current.y, midX, midY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(midX, midY);

    lastPos.current = pos;

    if (!hasStrokes) {
      setHasStrokes(true);
      onChange?.(canvas.toDataURL("image/png"));
    }
  };

  const endDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    lastPos.current = null;
    if (hasStrokes) {
      onChange?.(canvasRef.current?.toDataURL("image/png") || null);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      style={{
        width,
        height,
        display: "block",
        cursor: "crosshair",
        touchAction: "none",
      }}
      onMouseDown={startDraw}
      onMouseMove={draw}
      onMouseUp={endDraw}
      onMouseLeave={endDraw}
      onTouchStart={startDraw}
      onTouchMove={draw}
      onTouchEnd={endDraw}
    />
  );
});

SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
